import { Injectable } from '@angular/core';
import { BaseService } from './base';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { IndexedDBService } from '@vex/services/indexeddb.service';

/** Live progress emitted while a table snapshot is being prepared or downloaded. */
export interface SyncStatus {
  /** Clears tab-local presentation state after an explicit shared-cache reset. */
  reset?: boolean;
  /** Whether any related synchronization work remains active. */
  active: boolean;
  /** Whether this is the first population of the local table. */
  initialSync: boolean;
  /** IndexedDB object-store name. */
  tableName?: string;
  /** Human-readable table name used by the UI. */
  tableLabel?: string;
  /** Current user-facing progress or failure message. */
  message: string;
  /** One-based number of pages completed when page information is available. */
  pageNumber?: number;
  /** Total pages for legacy offset-based endpoints; unavailable for cursor paging. */
  totalPages?: number;
  /** Number of initial table snapshots still queued or running. */
  pendingTables?: number;
  /** Number of records returned so far for the current table. */
  returnedItemCount?: number;
  /** Changed records expected inside the table's fixed server snapshot. */
  expectedItemCount?: number;
  /** Total number of platform-data tables participating in workspace setup. */
  totalTables?: number;
  /** Indicates that this individual table passed validation and committed. */
  tableCompleted?: boolean;
  /** Indicates that the related initial-sync sequence finished successfully. */
  completed?: boolean;
  /** Indicates that the related synchronization failed. */
  error?: boolean;
}

/** Read-only diagnostic state for one synchronized IndexedDB table. */
export interface SyncTableDiagnostic {
  /** IndexedDB object-store and synchronization table name. */
  tableName: string;
  /** Human-readable label displayed in the diagnostics dialog. */
  tableLabel: string;
  /** Current runtime/checkpoint state for the table. */
  status: 'Syncing' | 'Waiting' | 'Ready' | 'Failed' | 'Repair needed' | 'Not synced' | 'Stopped';
  /** Current number of records in the corresponding IndexedDB object store. */
  recordCount: number;
  /** Server watermark committed only after a complete successful snapshot. */
  committedThrough?: string;
  /** Browser time at which the committed snapshot completed. */
  completedAt?: string;
  /** Client/server data contract version used for the checkpoint. */
  contractVersion?: number;
  /** Latest live progress message, when synchronization has emitted one. */
  message?: string;
  /** Latest completed page number, when available. */
  pageNumber?: number;
  /** Total legacy pages, when available. */
  totalPages?: number;
  /** Latest failed attempt recorded for recovery after refresh. */
  lastError?: string;
  /** Server count captured when the table was last validated. */
  serverRecordCount?: number;
}

/** Point-in-time browser cache and synchronization runtime diagnostics. */
export interface SyncDiagnostics {
  /** UTC time at which the diagnostic snapshot was collected. */
  capturedAt: string;
  /** Overall synchronization state in this browser tab. */
  state: 'Running' | 'Idle' | 'Stopped';
  /** Whether this tab owns cross-tab network synchronization. */
  tabRole: 'Leader' | 'Follower';
  /** Number of sync HTTP requests currently in flight. */
  activeRequests: number;
  /** Maximum sync HTTP requests allowed across tables in the leader tab. */
  concurrencyLimit: number;
  /** Number of requests waiting for a global concurrency slot. */
  queuedRequests: number;
  /** Number of initial table snapshots queued or running. */
  pendingInitialSyncs: number;
  /** Total records across synchronized IndexedDB object stores. */
  totalRecords: number;
  /** Number of tables with a persistent committed watermark. */
  readyTables: number;
  /** Total number of synchronized tables known by the client. */
  tableCount: number;
  /** Most recent successful checkpoint completion time across all tables. */
  lastCompletedAt?: string;
  /** Table-by-table diagnostics. */
  tables: SyncTableDiagnostic[];
}

/** Persistent checkpoint stored in the SyncMetadata IndexedDB object store. */
interface SyncMetadata {
  Id: string;
  CommittedThrough?: string;
  CompletedAt?: string;
  ContractVersion: number;
  Status?: 'Syncing' | 'Ready' | 'Failed' | 'RepairNeeded';
  LastAttemptAt?: string;
  LastError?: string;
  PendingSince?: string;
  PendingSnapshotThrough?: string;
  PendingAfterId?: string;
  ReceivedItemCount?: number;
  ExpectedItemCount?: number;
  TotalItemCount?: number;
  LastValidatedAt?: string;
}

/** Common backend response used by every platform-data table in contract v3. */
interface PlatformDataSyncPage {
  Items: any[];
  SnapshotThrough: string;
  NextAfterId?: string;
  HasMore: boolean;
  ExpectedItemCount?: number;
  TotalItemCount?: number;
  ContractVersion: number;
}

/** Final result for a login or user-triggered synchronization pass. */
export interface SyncAllResult {
  succeeded: string[];
  failed: string[];
}

/** Distinguishes data-integrity failures from transient transport failures. */
class SyncValidationError extends Error {
  /** Stable discriminator because Error subclass instanceof is unreliable after ES5 transpilation. */
  readonly isSyncValidationError = true;
}

@Injectable({
  providedIn: 'root'
})

/**
 * Coordinates the global platform-data cache shared by browser tabs.
 *
 * One elected tab performs network synchronization. Every table commits a
 * persistent server watermark only after all pages in its snapshot have been
 * stored successfully. Other tabs read the shared IndexedDB database and wait
 * for leader completion notifications instead of downloading duplicate data.
 */
export class SyncService extends BaseService {
  /** Marker consumed by AuthInterceptor so route changes do not cancel durable sync work. */
  private readonly backgroundSyncRequestOptions = {
    headers: { 'X-Kumorai-Background-Sync': 'true' }
  };
  private readonly initialSyncDate = '1900-01-01';
  private pendingInitialSyncs = 0;
  private readonly syncStatusSubject = new Subject<SyncStatus>();
  /** Cancels background requests only for logout, never for ordinary navigation. */
  private readonly syncStopSubject = new Subject<void>();
  syncStatus$ = this.syncStatusSubject.asObservable();
  private latestTableStatus: { [tableName: string]: SyncStatus } = {};
  private readonly syncRequestRetryCount = 3;
  private readonly syncRequestRetryDelayMs = 750;
  private readonly syncLockTimeoutMs = 10 * 60 * 1000;
  private readonly syncLockPollMs = 1000;
  private readonly cacheResetKeyPrefix = 'kumorai-sync-cache-reset:';
  private readonly syncMessageKey = 'kumorai-sync-message';
  private readonly tabId = `${Date.now()}-${Math.random().toString(36).substring(2)}`;
  private readonly syncLeaderKey = 'kumorai-sync-leader';
  private readonly syncLeaderHeartbeatKey = 'kumorai-sync-leader-heartbeat';
  private readonly syncLeaderTimeoutMs = 15000;
  private readonly globalConcurrencyLimit = 3;
  private activeGlobalRequests = 0;
  private globalRequestWaiters: Array<() => void> = [];
  private syncChannel: BroadcastChannel = null;
  private syncLeaderTimer: any;
  private syncStopped = false;
  private startupRecoveryPromise: Promise<SyncAllResult>;
  private syncGeneration = 0;
  private startupRecoveryGeneration = -1;
  private leaderSyncWaiters: { [tableName: string]: Array<{ resolve: () => void; reject: (error: any) => void }> } = {};
  private readonly pageSize: { [key: string]: number } = {
    AttributeData: 500,
    DependencyMap: 100,
    Collection: 500,
    CollectionEntry: 25,
    TemplateCreator: 300,
    AttributeType: 100,
    Meta: 500,
    TransformationTemplate: 300,
  };
  /** Single source of truth shared by login, diagnostics, and manual recovery. */
  private readonly syncTables = [
    { endpoint: 'AttributeType', tableName: 'AttributeType' },
    { endpoint: 'AttributeData', tableName: 'AttributeData' },
    { endpoint: 'CollectionEntry', tableName: 'CollectionEntry' },
    { endpoint: 'Collection', tableName: 'Collection' },
    { endpoint: 'DependencyMap', tableName: 'DependencyMap' },
    { endpoint: 'TemplateCreator', tableName: 'TemplateCreator' },
    { endpoint: 'Meta', tableName: 'Meta' },
    { endpoint: 'TransformationTemplate', tableName: 'TransformationTemplate' }
  ];

  /** Returns the configured server page size for a synchronized table. */
  private getPageSize(tableName: string): number {
    return this.pageSize[tableName] || 250;
  }

  /** Returns the human-readable table label used by progress and error messages. */
  private getTableLabel(tableName: string): string {
    const labels: { [key: string]: string } = {
      AttributeData: 'attribute data',
      DependencyMap: 'dependency map',
      Collection: 'collections',
      CollectionEntry: 'collection entries',
      TemplateCreator: 'templates',
      AttributeType: 'attribute types',
      Meta: 'metadata',
      TransformationTemplate: 'transformation template',
    };

    return labels[tableName] || tableName;
  }

  /** Saves and publishes the latest live progress state for a table. */
  private emitSyncStatus(status: SyncStatus): void {
    if (status.tableName) {
      this.latestTableStatus[status.tableName] = status;
    }
    this.syncStatusSubject.next(status);
  }

  constructor(
    private http: HttpClient,
    private dbService: IndexedDBService,
    private toastr: ToastrService
  ) {
    super();
    if (typeof window !== 'undefined') {
      if (typeof BroadcastChannel !== 'undefined') {
        this.syncChannel = new BroadcastChannel('kumorai_sync_channel');
        this.syncChannel.onmessage = (event) => this.handleSyncChannelMessage(event.data);
      }
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key?.startsWith(this.cacheResetKeyPrefix) && event.newValue) {
          const tableName = event.key.substring(this.cacheResetKeyPrefix.length);
          this.handleExternalTableCacheReset(tableName);
        } else if (event.key === this.syncMessageKey && event.newValue) {
          try {
            this.handleSyncChannelMessage(JSON.parse(event.newValue));
          } catch (error) {
            console.error('Unable to process cross-tab sync message.', error);
          }
        }
      });
      // A table lock represents in-memory work owned by this tab. It cannot be
      // resumed by that owner after the document closes, so release both lease
      // layers synchronously and let another tab continue from IndexedDB metadata.
      window.addEventListener('beforeunload', () => this.releaseOwnedSyncLeases());
      window.addEventListener('online', () => this.resumeIncompleteSyncs());
      this.tryBecomeSyncLeader();
    }
  }

  /** Broadcasts a sync lifecycle message with tab identity and timestamp. */
  private postSyncMessage(message: any): void {
    const payload = { ...message, senderId: this.tabId, sentAt: Date.now() };
    if (this.syncChannel) {
      this.syncChannel.postMessage(payload);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.syncMessageKey, JSON.stringify(payload));
    }
  }

  /** Handles leader requests, completion notifications, failures, and logout stops. */
  private handleSyncChannelMessage(message: any): void {
    if (!message || message.senderId === this.tabId) {
      return;
    }

    if (message.type === 'sync-request' && this.isSyncLeader() && !this.syncStopped) {
      this.checkAndSyncData(message.baseEndpoint, message.tableName, !!message.force, !!message.repair).then(succeeded => {
        if (succeeded) {
          this.postSyncMessage({ type: 'sync-completed', tableName: message.tableName });
        } else {
          this.postSyncMessage({ type: 'sync-failed', tableName: message.tableName });
        }
      });
      return;
    }

    if (message.type === 'sync-completed' || message.type === 'sync-failed') {
      const waiters = this.leaderSyncWaiters[message.tableName] || [];
      delete this.leaderSyncWaiters[message.tableName];
      waiters.forEach(waiter => message.type === 'sync-completed'
        ? waiter.resolve()
        : waiter.reject(new Error(message.error || 'Sync failed in the leader tab.')));
      return;
    }

    if (message.type === 'sync-stop') {
      this.stopLocalSyncActivity();
      if (message.clearLocalData) {
        this.emitSyncStatus({ reset: true, active: false, initialSync: false, message: '' });
      }
    }
  }

  /** Returns true when this tab currently owns the shared sync-leader lease. */
  private isSyncLeader(): boolean {
    return typeof localStorage === 'undefined' || localStorage.getItem(this.syncLeaderKey) === this.tabId;
  }

  /**
   * Attempts to acquire the cross-tab leader lease when it is absent or stale.
   * The localStorage write/read check reduces simultaneous-claim races.
   */
  private tryBecomeSyncLeader(): boolean {
    if (typeof localStorage === 'undefined' || this.syncStopped) {
      return !this.syncStopped;
    }

    const now = Date.now();
    const leaderId = localStorage.getItem(this.syncLeaderKey);
    const heartbeat = Number(localStorage.getItem(this.syncLeaderHeartbeatKey)) || 0;
    if (!leaderId || leaderId === this.tabId || now - heartbeat > this.syncLeaderTimeoutMs) {
      localStorage.setItem(this.syncLeaderKey, this.tabId);
      localStorage.setItem(this.syncLeaderHeartbeatKey, `${now}`);
      if (localStorage.getItem(this.syncLeaderKey) === this.tabId) {
        // Pending cursor metadata makes an abandoned snapshot resumable. Once
        // this tab owns the browser-wide lease, locks from another owner can no
        // longer represent work coordinated by the elected sync leader.
        this.clearOrphanedTableLocks();
        this.startSyncLeaderHeartbeat();
        return true;
      }
    }

    return false;
  }

  /** Starts or refreshes the heartbeat that keeps this tab's leader lease alive. */
  private startSyncLeaderHeartbeat(): void {
    if (this.syncLeaderTimer) {
      clearInterval(this.syncLeaderTimer);
    }
    this.syncLeaderTimer = setInterval(() => {
      if (this.isSyncLeader() && !this.syncStopped) {
        localStorage.setItem(this.syncLeaderHeartbeatKey, `${Date.now()}`);
      }
    }, 5000);
  }

  /** Stops the heartbeat and releases the shared leader lease owned by this tab. */
  private releaseSyncLeadership(): void {
    if (this.syncLeaderTimer) {
      clearInterval(this.syncLeaderTimer);
      this.syncLeaderTimer = undefined;
    }
    if (typeof localStorage !== 'undefined' && this.isSyncLeader()) {
      localStorage.removeItem(this.syncLeaderKey);
      localStorage.removeItem(this.syncLeaderHeartbeatKey);
    }
  }

  /** Releases every table lock owned by this tab followed by its leader lease. */
  private releaseOwnedSyncLeases(): void {
    Object.keys(this.pageSize).forEach(tableName => this.releaseSyncLock(tableName));
    this.releaseSyncLeadership();
  }

  /** Removes table locks whose owner is no longer the elected sync leader. */
  private clearOrphanedTableLocks(): void {
    if (typeof localStorage === 'undefined' || !this.isSyncLeader()) {
      return;
    }

    Object.keys(this.pageSize).forEach(tableName => {
      const lock = this.getSyncLock(tableName);
      if (lock && lock.ownerId !== this.tabId) {
        localStorage.removeItem(this.getSyncLockKey(tableName));
      }
    });
  }

  /**
   * Requests a table sync from the leader and resolves when completion is broadcast.
   * If the leader becomes stale, this tab attempts takeover before failing the wait.
   */
  private waitForLeaderSync(baseEndpoint: string, tableName: string, force: boolean = false, repair: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.leaderSyncWaiters[tableName]) {
        this.leaderSyncWaiters[tableName] = [];
      }
      this.leaderSyncWaiters[tableName].push({ resolve, reject });
      this.postSyncMessage({
        type: 'sync-request',
        baseEndpoint,
        tableName,
        force,
        repair
      });

      setTimeout(() => {
        const waiters = this.leaderSyncWaiters[tableName] || [];
        const waiterIndex = waiters.findIndex(item => item.resolve === resolve);
        if (waiterIndex >= 0) {
          waiters.splice(waiterIndex, 1);
          if (this.tryBecomeSyncLeader()) {
            this.checkAndSyncData(baseEndpoint, tableName, force, repair).then(succeeded => succeeded
              ? resolve()
              : reject(new Error(`Unable to sync ${tableName}.`))).catch(reject);
          } else {
            reject(new Error(`Timed out waiting for the sync leader to finish ${tableName}.`));
          }
        }
      }, this.syncLeaderTimeoutMs + 5000);
    });
  }

  /** Runs work after obtaining one of the global sync HTTP concurrency slots. */
  private async withGlobalRequestSlot<T>(work: () => Promise<T>): Promise<T> {
    if (this.activeGlobalRequests >= this.globalConcurrencyLimit) {
      await new Promise<void>(resolve => this.globalRequestWaiters.push(resolve));
    }
    if (this.syncStopped) {
      throw new Error('Sync stopped during logout.');
    }
    this.activeGlobalRequests++;
    try {
      return await work();
    } finally {
      this.activeGlobalRequests = Math.max(0, this.activeGlobalRequests - 1);
      const next = this.globalRequestWaiters.shift();
      if (next) {
        next();
      }
    }
  }

  /**
   * Reads only a watermark produced by a fully validated snapshot.
   *
   * Local records without metadata may be remnants of an interrupted initial
   * download. Deriving a watermark from them would permanently skip missing pages.
   */
  private async getLastFetchedDate(tableName): Promise<string> {
    try {
      const metadata = await this.getSyncMetadata(tableName);
      if (metadata && metadata.CommittedThrough) {
        return metadata.CommittedThrough;
      }
    } catch(error) {
      // Missing metadata is the expected state for a first or interrupted sync.
    }
    return this.initialSyncDate;
  }

  /** Persists a server page into its IndexedDB table using keyed upserts. */
  addDataInIndexDB(values: any[], type: string): Promise<any[]> {
    return this.dbService.upsertAll(type, values);
  }

  /** Returns whether a failed request is safe to retry with exponential backoff. */
  private isTransientSyncError(error: any): boolean {
    const status = Number(error && error.status || 0);
    return status === 0 || status === 408 || status === 429 || status === 500 ||
      status === 502 || status === 503 || status === 504;
  }

  /** Returns a promise that resolves after the requested backoff interval. */
  private delay(milliseconds: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }

  /** Executes any sync request with the same transient-error and Retry-After policy. */
  private async executeSyncRequestWithRetry<T>(request: () => Promise<T>): Promise<T> {
    let lastError: any;
    for (let attempt = 1; attempt <= this.syncRequestRetryCount; attempt++) {
      try {
        return await request();
      } catch (error) {
        lastError = error;
        if (attempt === this.syncRequestRetryCount || !this.isTransientSyncError(error)) {
          break;
        }
        const retryAfter = Number(error?.headers?.get?.('Retry-After'));
        const backoff = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : this.syncRequestRetryDelayMs * Math.pow(2, attempt - 1);
        await this.delay(backoff + Math.floor(Math.random() * 250));
      }
    }
    throw lastError;
  }

  /**
   * Downloads or resumes a contract-v3 snapshot and validates every persisted page.
   * Pending cursor state is durable, while CommittedThrough remains unchanged until
   * the expected count and final local count have both been verified.
   */
  private async syncPlatformDataSnapshot(
    baseEndpoint: string,
    tableName: string,
    since: string,
    onProgress?: (completedPages: number, returnedItemCount: number, expectedItemCount?: number) => void
  ): Promise<{ snapshotThrough: string; totalItemCount: number; localItemCount: number }> {
    let metadata = await this.getSyncMetadata(tableName).catch(() => null);
    const canResume = !!metadata?.PendingSnapshotThrough && metadata.PendingSince === since;
    let snapshotThrough = canResume ? metadata.PendingSnapshotThrough : null;
    let afterId = canResume ? metadata.PendingAfterId : null;
    let receivedItemCount = canResume ? (metadata.ReceivedItemCount || 0) : 0;
    let expectedItemCount = canResume ? metadata.ExpectedItemCount : null;
    let totalItemCount = canResume ? metadata.TotalItemCount : null;
    let completedPages = 0;

    if (this.isInitialSync(since) && !canResume) {
      // Old clients may have left a partial table without metadata. A new v3
      // initial run starts clean so its server count proves exact membership.
      await this.dbService.clearStore(tableName);
    }

    do {
      this.refreshSyncLock(tableName);
      const response = await this.executeSyncRequestWithRetry(() =>
        this.withGlobalRequestSlot(() => this.http.post<PlatformDataSyncPage>(
          `${this.baseUrl}${baseEndpoint}/SyncPage`,
          {
            Since: since,
            SnapshotThrough: snapshotThrough,
            AfterId: afterId,
            PageSize: this.getPageSize(tableName)
          },
          this.backgroundSyncRequestOptions
        ).pipe(takeUntil(this.syncStopSubject)).toPromise())
      );
      if (!response?.SnapshotThrough || response.ContractVersion !== 3) {
        throw new Error(`${tableName} returned an unsupported sync snapshot.`);
      }
      if (snapshotThrough && response.SnapshotThrough !== snapshotThrough) {
        throw new Error(`${tableName} changed its snapshot boundary while paging.`);
      }

      snapshotThrough = response.SnapshotThrough;
      expectedItemCount = response.ExpectedItemCount ?? expectedItemCount;
      totalItemCount = response.TotalItemCount ?? totalItemCount;
      if (expectedItemCount == null || totalItemCount == null) {
        throw new Error(`${tableName} did not return validation counts for its first page.`);
      }

      const items = response.Items || [];
      await this.addDataInIndexDB(items, tableName);
      receivedItemCount += items.length;
      completedPages++;
      const nextAfterId = response.HasMore ? response.NextAfterId : null;
      if (response.HasMore && (!nextAfterId || nextAfterId === afterId)) {
        throw new Error(`${tableName} sync cursor did not advance.`);
      }
      afterId = nextAfterId;

      await this.dbService.upsert('SyncMetadata', {
        ...(metadata || { Id: tableName }),
        Id: tableName,
        ContractVersion: 3,
        Status: 'Syncing',
        LastAttemptAt: new Date().toISOString(),
        LastError: null,
        PendingSince: since,
        PendingSnapshotThrough: snapshotThrough,
        PendingAfterId: afterId,
        ReceivedItemCount: receivedItemCount,
        ExpectedItemCount: expectedItemCount,
        TotalItemCount: totalItemCount
      } as SyncMetadata);
      metadata = await this.getSyncMetadata(tableName);
      onProgress?.(completedPages, receivedItemCount, expectedItemCount);

      if (!response.HasMore) {
        break;
      }
    } while (!this.syncStopped);

    if (this.syncStopped) {
      throw new Error('Sync stopped during logout.');
    }
    if (receivedItemCount !== expectedItemCount) {
      throw new SyncValidationError(`${tableName} returned ${receivedItemCount} of ${expectedItemCount} expected records.`);
    }

    const localItemCount = await this.dbService.count(tableName);
    return { snapshotThrough, totalItemCount, localItemCount };
  }

  /** Builds the localStorage key for a table's cross-tab sync lock. */
  private getSyncLockKey(tableName: string): string {
    return `kumorai-sync-lock:${tableName}`;
  }

  /** Builds the localStorage key for a table's last successful shared sync time. */
  private getSyncTimestampKey(tableName: string): string {
    return `kumorai-sync-last:${tableName}`;
  }

  /** Reads the last successful shared sync time, returning zero when unavailable. */
  private getSharedLastSyncTime(tableName: string): number {
    if (typeof localStorage === 'undefined') {
      return 0;
    }

    const value = Number(localStorage.getItem(this.getSyncTimestampKey(tableName)));
    return isNaN(value) ? 0 : value;
  }

  /** Publishes a table's last successful sync time for other tabs. */
  private setSharedLastSyncTime(tableName: string, syncTime: number): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.getSyncTimestampKey(tableName), `${syncTime}`);
  }

  /**
   * Reads and validates a table lock, removing malformed or expired lock records.
   * Returns null when no active lock exists.
   */
  private getSyncLock(tableName: string): any {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const rawLock = localStorage.getItem(this.getSyncLockKey(tableName));
    if (!rawLock) {
      return null;
    }

    try {
      return JSON.parse(rawLock);
    } catch (error) {
      localStorage.removeItem(this.getSyncLockKey(tableName));
      return null;
    }
  }

  /** Writes or renews a table lock owned by this tab. */
  private writeSyncLock(tableName: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(this.getSyncLockKey(tableName), JSON.stringify({
      ownerId: this.tabId,
      expiresAt: Date.now() + this.syncLockTimeoutMs
    }));
  }

  /** Attempts to claim a table lock and verifies ownership after writing it. */
  private acquireSyncLock(tableName: string): boolean {
    if (typeof localStorage === 'undefined') {
      return true;
    }

    const lock = this.getSyncLock(tableName);
    if (lock && lock.ownerId !== this.tabId && lock.expiresAt > Date.now()) {
      return false;
    }

    this.writeSyncLock(tableName);
    const savedLock = this.getSyncLock(tableName);
    return savedLock && savedLock.ownerId === this.tabId;
  }

  /** Extends a table lock only when this tab still owns it. */
  private refreshSyncLock(tableName: string): void {
    const lock = this.getSyncLock(tableName);
    if (lock && lock.ownerId === this.tabId) {
      this.writeSyncLock(tableName);
    }
  }

  /** Removes a table lock only when this tab owns it. */
  private releaseSyncLock(tableName: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const lock = this.getSyncLock(tableName);
    if (lock && lock.ownerId === this.tabId) {
      localStorage.removeItem(this.getSyncLockKey(tableName));
    }
  }

  /** Polls until another tab releases its table lock or the lock expires. */
  private async waitForSyncLock(tableName: string): Promise<void> {
    let lock = this.getSyncLock(tableName);
    while (lock && lock.ownerId !== this.tabId && lock.expiresAt > Date.now()) {
      await this.delay(this.syncLockPollMs);
      lock = this.getSyncLock(tableName);
    }
  }

  /** Returns whether a watermark represents an uninitialized table. */
  private isInitialSync(maxDate: string): boolean {
    return (maxDate || '').startsWith(this.initialSyncDate);
  }

  /** Re-enables scheduling and attempts leadership after a successful login. */
  resumeAfterLogin(): void {
    this.syncStopped = false;
    this.tryBecomeSyncLeader();
  }

  /**
   * Reconciles platform data whenever an authenticated application shell starts.
   *
   * This covers refreshes and replacement tabs where the previous sync owner was
   * closed mid-snapshot. Healthy tables retain the five-minute shared freshness
   * shortcut; incomplete tables resume their persisted snapshot cursor and every
   * table that runs must pass server/local count validation before becoming Ready.
   */
  resumeOnApplicationStart(): Promise<SyncAllResult> {
    if (!this.startupRecoveryPromise || this.startupRecoveryGeneration !== this.syncGeneration) {
      this.resumeAfterLogin();
      const recoveryGeneration = this.syncGeneration;
      this.startupRecoveryGeneration = recoveryGeneration;
      const recoveryPromise = (async () => {
        // Interrupted metadata takes precedence over the short cross-tab
        // freshness cache. Recover it first, then reconcile every healthy or
        // not-yet-created table through the normal login orchestration path.
        await this.resumeIncompleteSyncs();
        return this.syncAll(false);
      });
      this.startupRecoveryPromise = recoveryPromise();
      this.startupRecoveryPromise.finally(() => {
        if (this.startupRecoveryGeneration === recoveryGeneration) {
          this.startupRecoveryPromise = null;
          this.startupRecoveryGeneration = -1;
        }
      });
    }
    return this.startupRecoveryPromise;
  }

  /**
   * Stops new sync work before authentication is removed.
   *
   * @param clearLocalData Clears all shared IndexedDB stores when true.
   * @param broadcast Notifies other tabs to stop their local schedulers when true.
   */
  async prepareForLogout(clearLocalData: boolean, broadcast: boolean = true): Promise<void> {
    this.stopLocalSyncActivity();
    if (broadcast) {
      this.postSyncMessage({ type: 'sync-stop', clearLocalData });
    }
    if (clearLocalData) {
      await this.dbService.clearAllStores();
      this.emitSyncStatus({ reset: true, active: false, initialSync: false, message: '' });
    }
  }

  /**
   * Marks this tab stopped, releases leadership and table locks, and wakes queued
   * request waiters so they can observe the stopped state instead of hanging.
   */
  private stopLocalSyncActivity(): void {
    this.syncGeneration++;
    this.syncStopped = true;
    this.syncStopSubject.next();
    this.startupRecoveryPromise = null;
    this.startupRecoveryGeneration = -1;
    this.latestTableStatus = {};
    this.releaseSyncLeadership();
    Object.keys(this.pageSize).forEach(tableName => this.releaseSyncLock(tableName));
    this.globalRequestWaiters.splice(0).forEach(resolve => resolve());
  }

  // Check if sync is needed and trigger sync
  private syncPromiseData: any = <any>{};
  private syncPromiseGeneration: any = <any>{};
  lastSyncTime: any = {};
  /**
   * Clears one table and its persistent checkpoint, then notifies other tabs.
   * The next access performs a full snapshot for that table.
   */
  async resetTableCache(tableName: string): Promise<void> {
    const activeSync = this.syncPromiseData[tableName];
    if (activeSync) {
      try {
        await activeSync;
      } catch (error) {
        // The cache is cleared even if the in-flight sync failed.
      }
    }

    await this.dbService.clearStore(tableName);
    await this.dbService.delete('SyncMetadata', tableName);
    this.resetTableSyncState(tableName);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        this.cacheResetKeyPrefix + tableName,
        `${Date.now()}-${this.tabId}`
      );
    }
  }

  /** Reads one table's persistent checkpoint by its primary key. */
  private getSyncMetadata(tableName: string): Promise<SyncMetadata> {
    return this.dbService.get('SyncMetadata', tableName);
  }

  /** Stores a successfully validated table watermark and removes its resumable cursor. */
  private saveSyncMetadata(tableName: string, committedThrough: string, totalItemCount: number): Promise<any> {
    return this.dbService.upsert('SyncMetadata', {
      Id: tableName,
      CommittedThrough: committedThrough,
      CompletedAt: new Date().toISOString(),
      ContractVersion: 3,
      Status: 'Ready',
      LastAttemptAt: new Date().toISOString(),
      LastError: null,
      PendingSince: null,
      PendingSnapshotThrough: null,
      PendingAfterId: null,
      ReceivedItemCount: null,
      ExpectedItemCount: null,
      TotalItemCount: totalItemCount,
      LastValidatedAt: new Date().toISOString()
    } as SyncMetadata);
  }

  /** Persists a failed attempt without discarding the last known-good watermark or resumable cursor. */
  private async saveSyncFailure(tableName: string, error: any): Promise<void> {
    const metadata = await this.getSyncMetadata(tableName).catch(() => null);
    await this.dbService.upsert('SyncMetadata', {
      ...(metadata || { Id: tableName, ContractVersion: 3 }),
      Id: tableName,
      ContractVersion: 3,
      Status: 'Failed',
      LastAttemptAt: new Date().toISOString(),
      LastError: error?.message || 'Synchronization failed.'
    } as SyncMetadata);
  }

  /**
   * Returns read-only, point-in-time sync and IndexedDB diagnostics.
   * Calling this method never starts synchronization or mutates cached records.
   */
  async getDiagnostics(): Promise<SyncDiagnostics> {
    await this.dbService.initDB();
    const tableNames = Object.keys(this.pageSize);
    const tables = await Promise.all(tableNames.map(async tableName => {
      const [metadata, recordCount] = await Promise.all([
        this.getSyncMetadata(tableName).catch(() => null),
        this.dbService.count(tableName).catch(() => 0)
      ]);
      const runtimeStatus = this.latestTableStatus[tableName];
      const isSyncing = !!this.syncPromiseData[tableName];
      const isWaiting = (this.leaderSyncWaiters[tableName] || []).length > 0;
      let status: SyncTableDiagnostic['status'];
      if (this.syncStopped) {
        status = 'Stopped';
      } else if (isSyncing) {
        status = 'Syncing';
      } else if (isWaiting) {
        status = 'Waiting';
      } else if (metadata?.Status === 'Failed') {
        status = 'Failed';
      } else if (metadata?.Status === 'RepairNeeded') {
        status = 'Repair needed';
      } else {
        status = metadata?.Status === 'Ready' ? 'Ready' : 'Not synced';
      }

      return {
        tableName,
        tableLabel: this.getTableLabel(tableName),
        status,
        recordCount,
        committedThrough: metadata && metadata.CommittedThrough,
        completedAt: metadata && metadata.CompletedAt,
        contractVersion: metadata && metadata.ContractVersion,
        message: (isSyncing || isWaiting || status === 'Ready') && runtimeStatus && runtimeStatus.message,
        pageNumber: runtimeStatus && runtimeStatus.pageNumber,
        totalPages: runtimeStatus && runtimeStatus.totalPages,
        lastError: metadata && metadata.LastError,
        serverRecordCount: metadata && metadata.TotalItemCount
      } as SyncTableDiagnostic;
    }));
    const completedDates = tables
      .map(table => table.completedAt)
      .filter(value => !!value)
      .sort();
    const hasActiveSync = tables.some(table => table.status === 'Syncing' || table.status === 'Waiting');

    return {
      capturedAt: new Date().toISOString(),
      state: this.syncStopped ? 'Stopped' : (hasActiveSync || this.activeGlobalRequests > 0 ? 'Running' : 'Idle'),
      tabRole: this.isSyncLeader() ? 'Leader' : 'Follower',
      activeRequests: this.activeGlobalRequests,
      concurrencyLimit: this.globalConcurrencyLimit,
      queuedRequests: this.globalRequestWaiters.length,
      pendingInitialSyncs: this.pendingInitialSyncs,
      totalRecords: tables.reduce((total, table) => total + table.recordCount, 0),
      readyTables: tables.filter(table => !!table.committedThrough).length,
      tableCount: tables.length,
      lastCompletedAt: completedDates.length > 0 ? completedDates[completedDates.length - 1] : undefined,
      tables
    };
  }

  /**
   * Applies a cache-reset notification received from another tab after waiting
   * for any local in-flight work to settle.
   */
  private async handleExternalTableCacheReset(tableName: string): Promise<void> {
    const activeSync = this.syncPromiseData[tableName];
    if (activeSync) {
      try {
        await activeSync;
      } catch (error) {
        // Clear the shared store after any in-flight work has settled.
      }
    }

    try {
      await this.dbService.clearStore(tableName);
      await this.dbService.delete('SyncMetadata', tableName);
      this.resetTableSyncState(tableName);
    } catch (error) {
      console.error(`Unable to reset ${tableName} cache in this tab.`, error);
    }
  }

  /** Clears in-memory timestamps/promises and shared lock state for one table. */
  private resetTableSyncState(tableName: string): void {
    delete this.lastSyncTime[tableName];
    delete this.lastSyncTime[tableName + 'UpdateBySelf'];
    delete this.syncPromiseData[tableName];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.getSyncTimestampKey(tableName));
      localStorage.removeItem(this.getSyncLockKey(tableName));
    }
  }

  /**
   * Ensures a table is current while deduplicating calls within and across tabs.
   *
   * AttributeData uses a fixed snapshot and cursor. Remaining tables use their
   * legacy page endpoints under the same leader, lock, retry, and concurrency
   * controls. A persistent checkpoint is written only after successful completion.
   *
   * @returns True when data is current or synchronization succeeded; otherwise false.
   */
  async checkAndSyncData(
    baseEndpoint: string = 'Sync',
    tableName: string,
    force: boolean = false,
    repair: boolean = false
  ): Promise<boolean> {
    if (this.syncStopped) {
      return false;
    }
    if (!this.isSyncLeader() && !this.tryBecomeSyncLeader()) {
      return this.waitForLeaderSync(baseEndpoint, tableName, force, repair).then(async () => {
        const metadata = await this.getSyncMetadata(tableName).catch(() => null);
        return metadata?.Status === 'Ready';
      }).catch(() => {
        this.toastr.error(`Sync failed for ${this.getTableLabel(tableName)}. Please try again later.`, 'Error');
        return false;
      });
    }
    if (this.syncPromiseData[tableName] && this.syncPromiseGeneration[tableName] === this.syncGeneration) {
      return this.syncPromiseData[tableName];
    }
    // A cancelled promise from the preceding login generation must never block
    // the new session's first synchronization pass.
    delete this.syncPromiseData[tableName];
    delete this.syncPromiseGeneration[tableName];

    let FIVE_MINUTES = 5 * 60 * 1000;
    let now = Date.now();
    if (!force && !this.lastSyncTime[tableName+'UpdateBySelf'] && this.lastSyncTime[tableName] && (now - this.lastSyncTime[tableName]) < FIVE_MINUTES) {
      return true;
    }
    const sharedLastSyncTime = this.getSharedLastSyncTime(tableName);
    if (!force && !this.lastSyncTime[tableName+'UpdateBySelf'] && sharedLastSyncTime && (now - sharedLastSyncTime) < FIVE_MINUTES) {
      this.lastSyncTime[tableName] = sharedLastSyncTime;
      return true;
    }
    this.lastSyncTime[tableName+'UpdateBySelf'] = false;
    const syncGeneration = this.syncGeneration;
    const syncPromise = (async () => {
      let isInitialSyncRequest = false;
      let ownsSyncLock = false;
      try {
        await this.dbService.initDB();
        if (!this.acquireSyncLock(tableName)) {
          const tableLabel = this.getTableLabel(tableName);
          this.emitSyncStatus({
            active: true,
            initialSync: true,
            tableName,
            tableLabel,
            message: `Waiting for another tab to finish syncing ${tableLabel}.`
          });
          await this.waitForSyncLock(tableName);
          const completedMetadata = await this.getSyncMetadata(tableName).catch(() => null);
          if (completedMetadata?.Status === 'Ready' && completedMetadata.CommittedThrough) {
            this.lastSyncTime[tableName] = now;
            this.setSharedLastSyncTime(tableName, now);
            this.emitSyncStatus({
              active: false,
              initialSync: true,
              tableName,
              tableLabel,
              message: `Finished ${tableLabel}.`,
              completed: true
            });
            return true;
          }

          if (!this.acquireSyncLock(tableName)) {
            return false;
          }
        }

        ownsSyncLock = true;
        if (repair) {
          // A repair intentionally discards the suspect table and checkpoint. The
          // v3 pending cursor then makes an interrupted rebuild resumable.
          await this.dbService.clearStore(tableName);
          await this.dbService.delete('SyncMetadata', tableName);
        }
        let maxDate = await this.getLastFetchedDate(tableName);
        const isInitialSync = this.isInitialSync(maxDate);
        isInitialSyncRequest = isInitialSync;
        const tableLabel = this.getTableLabel(tableName);
        let totalPages: number = null;

        if (!isInitialSync) {
          this.toastr.info(`Sync started. Please wait...`, 'Syncing');
        }

        const progress = isInitialSync
          ? (completedPages: number, returnedItemCount: number, expectedItemCount?: number) => this.emitSyncStatus({
            active: true,
            initialSync: true,
            tableName,
            tableLabel,
            message: `Syncing ${tableLabel}. This can take a few minutes the first time.`,
            pageNumber: completedPages,
            totalPages,
            pendingTables: this.pendingInitialSyncs,
            returnedItemCount,
            expectedItemCount,
            totalTables: this.syncTables.length
          })
          : undefined;

        const syncWork = async () => this.syncPlatformDataSnapshot(baseEndpoint, tableName, maxDate, progress);
        let result: { snapshotThrough: string; totalItemCount: number; localItemCount: number };
        let validationRepairAttempted = repair;
        const syncWithValidationRepair = async () => {
          try {
            return await syncWork();
          } catch (error) {
            if (!error?.isSyncValidationError || validationRepairAttempted) {
              throw error;
            }
            // Retry a count-integrity failure from a new full snapshot once.
            // Transport/protocol failures retain the current cache and cursor.
            validationRepairAttempted = true;
            await this.dbService.clearStore(tableName);
            await this.dbService.delete('SyncMetadata', tableName);
            maxDate = this.initialSyncDate;
            return this.syncPlatformDataSnapshot(baseEndpoint, tableName, maxDate, progress);
          }
        };

        if (isInitialSync) {
          this.pendingInitialSyncs++;
          this.emitSyncStatus({
            active: true,
            initialSync: true,
            tableName,
            tableLabel,
            message: `Preparing ${tableLabel} for offline use.`,
            pageNumber: 0,
            totalPages,
            pendingTables: this.pendingInitialSyncs,
            totalTables: this.syncTables.length
          });
          try {
            // Tables may progress independently, while withGlobalRequestSlot()
            // enforces the single service-wide request budget. This avoids both
            // seven-table serialization and unbounded page-request bursts.
            result = await syncWithValidationRepair();
          } finally {
            this.pendingInitialSyncs = Math.max(this.pendingInitialSyncs - 1, 0);
          }
        } else {
          result = await syncWithValidationRepair();
        }

        if (result.localItemCount !== result.totalItemCount && !this.isInitialSync(maxDate)) {
          // Incremental snapshots cannot express physical deletions. A count
          // mismatch triggers one bounded full-table rebuild using the same fixed
          // snapshot contract, rather than repeatedly resyncing every login.
          await this.dbService.clearStore(tableName);
          await this.dbService.delete('SyncMetadata', tableName);
          maxDate = this.initialSyncDate;
          validationRepairAttempted = true;
          result = await this.syncPlatformDataSnapshot(baseEndpoint, tableName, maxDate, progress);
        }
        if (result.localItemCount !== result.totalItemCount) {
          throw new SyncValidationError(`${tableName} validation failed: local count ${result.localItemCount}, server count ${result.totalItemCount}.`);
        }

        if (this.syncStopped || syncGeneration !== this.syncGeneration) {
          return false;
        }
        await this.saveSyncMetadata(tableName, result.snapshotThrough, result.totalItemCount);
        if (this.syncStopped || syncGeneration !== this.syncGeneration) {
          return false;
        }
        this.lastSyncTime[tableName] = now;
        this.setSharedLastSyncTime(tableName, now);
        this.emitSyncStatus({
          active: isInitialSyncRequest && this.pendingInitialSyncs > 0,
          initialSync: isInitialSyncRequest,
          tableName,
          tableLabel,
          message: isInitialSyncRequest && this.pendingInitialSyncs > 0
            ? `Finished ${tableLabel}. Continuing workspace setup.`
            : `Finished syncing ${tableLabel}.`,
          pendingTables: this.pendingInitialSyncs,
          returnedItemCount: result.totalItemCount,
          expectedItemCount: result.totalItemCount,
          totalTables: this.syncTables.length,
          tableCompleted: true,
          completed: isInitialSyncRequest && this.pendingInitialSyncs === 0
        });
        return true;
      } catch (error) {
        if (this.syncStopped) {
          // Logout owns the terminal state. Do not recreate SyncMetadata after
          // an explicit clear-data transaction or display a misleading failure.
          return false;
        }
        await this.saveSyncFailure(tableName, error).catch(metadataError =>
          console.error(`Unable to save ${tableName} sync failure metadata.`, metadataError));
        this.emitSyncStatus({
          active: isInitialSyncRequest && this.pendingInitialSyncs > 0,
          initialSync: isInitialSyncRequest,
          tableName,
          tableLabel: this.getTableLabel(tableName),
          message: `Sync failed for ${this.getTableLabel(tableName)}. Review details or retry.`,
          pendingTables: this.pendingInitialSyncs,
          totalTables: this.syncTables.length,
          error: true
        });
        this.postSyncMessage({
          type: 'sync-failed',
          tableName,
          error: error && error.message
        });
        this.toastr.error(`Sync failed. Please try again later.`, 'Error');
        return false;
      } finally {
        if (ownsSyncLock) {
          this.releaseSyncLock(tableName);
        }
        if (this.syncPromiseData[tableName] === syncPromise) {
          delete this.syncPromiseData[tableName];
          delete this.syncPromiseGeneration[tableName];
        }
      }
    })();
    this.syncPromiseData[tableName] = syncPromise;
    this.syncPromiseGeneration[tableName] = syncGeneration;
    return syncPromise;
  }

  /**
   * Runs the same coordinated synchronization used after login for every global
   * platform-data table. One failed table does not cancel independent tables.
   */
  async syncAll(force: boolean = false): Promise<SyncAllResult> {
    this.resumeAfterLogin();
    const results = await Promise.all(this.syncTables.map(async table => ({
      tableName: table.tableName,
      succeeded: await this.checkAndSyncData(table.endpoint, table.tableName, force)
    })));
    return {
      succeeded: results.filter(item => item.succeeded).map(item => item.tableName),
      failed: results.filter(item => !item.succeeded).map(item => item.tableName)
    };
  }

  /** Retries only tables whose durable checkpoint indicates incomplete work. */
  async retryIncompleteTables(): Promise<SyncAllResult> {
    this.resumeAfterLogin();
    await this.dbService.initDB();
    const incompleteTables = (await Promise.all(this.syncTables.map(async table => ({
      table,
      metadata: await this.getSyncMetadata(table.tableName).catch(() => null)
    })))).filter(item => !item.metadata || item.metadata.Status !== 'Ready');

    const results = await Promise.all(incompleteTables.map(async item => ({
      tableName: item.table.tableName,
      succeeded: await this.checkAndSyncData(item.table.endpoint, item.table.tableName, true)
    })));
    return {
      succeeded: results.filter(item => item.succeeded).map(item => item.tableName),
      failed: results.filter(item => !item.succeeded).map(item => item.tableName)
    };
  }

  /**
   * Resumes only interrupted tables when connectivity returns. A stopped service
   * represents logout, so an online event can never silently restart sync then.
   */
  private async resumeIncompleteSyncs(): Promise<void> {
    if (this.syncStopped) {
      return;
    }
    await this.dbService.initDB();
    await Promise.all(this.syncTables.map(async table => {
      const metadata = await this.getSyncMetadata(table.tableName).catch(() => null);
      if (metadata?.Status === 'Failed' || metadata?.Status === 'Syncing' || metadata?.Status === 'RepairNeeded') {
        // A recently completed older checkpoint must not hide a newer failed or
        // interrupted attempt during startup/reconnect recovery.
        delete this.lastSyncTime[table.tableName];
        delete this.lastSyncTime[table.tableName + 'UpdateBySelf'];
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(this.getSyncTimestampKey(table.tableName));
        }
        await this.checkAndSyncData(table.endpoint, table.tableName, true);
      }
    }));
  }

  /** Clears and rebuilds one table when diagnostics identify a validation problem. */
  async repairTable(tableName: string): Promise<boolean> {
    const table = this.syncTables.find(item => item.tableName === tableName);
    return table ? this.checkAndSyncData(table.endpoint, table.tableName, true, true) : false;
  }

  /** Reads all locally cached records for a synchronized table. */
  async sync(type: string = 'DependencyMap'): Promise<any[]> {
    try {
      const localData: any[] = await this.dbService.getAll(type);

      if (localData && localData.length > 0) {
        return localData;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }
}
